import React, { forwardRef, useRef, useState, useEffect, useImperativeHandle } from "react";
import Spinner from "components/Common/Spinner/index.js";
import { getButtonStyles } from "../Button.tsx";
import DropdownMenu from "./DropdownMenu.tsx";

export interface DropdownOption {
  label: string;
  onClick?: () => void;
  to?: string;
  link?: string;
  icon?: React.ReactElement;
  disabled?: boolean;
  className?: string;
}

export interface DropdownButtonProps {
  text: string;
  color?: "default" | "primary" | "secondary" | "outline" | "disabled";
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement;
  dropdownOptions: DropdownOption[];
  disabled?: boolean;
  className?: string;
  baseStyle?: React.CSSProperties;
  isLoading?: boolean;
}

const DropdownButton = forwardRef<HTMLElement, DropdownButtonProps>(({
  text,
  color = "default",
  iconLeft,
  iconRight,
  dropdownOptions,
  disabled = false,
  className = "",
  baseStyle = {},
  isLoading
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => buttonRef.current as HTMLElement);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === "Escape") {
      closeDropdown();
    } else if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const updateButtonRect = () => {
      if (buttonRef.current && isOpen) {
        setButtonRect(buttonRef.current.getBoundingClientRect());
      }
    };

    updateButtonRect();

    const listenerOptions = { passive: true };

    try {
      window.addEventListener("resize", updateButtonRect, listenerOptions);
      window.addEventListener("scroll", updateButtonRect, listenerOptions);
    } catch (e) {
      window.addEventListener("resize", updateButtonRect);
      window.addEventListener("scroll", updateButtonRect);
    }

    return () => {
      try {
        window.removeEventListener("resize", updateButtonRect);
        window.removeEventListener("scroll", updateButtonRect);
      } catch (e) {
        window.removeEventListener("resize", updateButtonRect);
        window.removeEventListener("scroll", updateButtonRect);
      }
    };
  }, [isOpen]);

  return (
    <div className={`inline-block ${disabled ? "cursor-not-allowed" : ""}`}>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        className={`${className} ${disabled
          ? "px-4 rounded-full font-medium flex items-center gap-2 w-fit leading-5 font-poppins text-base bg-disabled-button border-disabled-button text-white cursor-not-allowed pointer-events-none"
          : getButtonStyles(color)}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        disabled={disabled}
        style={baseStyle}
        type="button"
      >
        {iconLeft && iconLeft}
        {
          isLoading
            ? <div><Spinner color="#FFF" size={6} /></div>
            : <span>{text}</span>
        }
        {iconRight || (
          <svg
            className="w-4 h-4 ml-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </button>

      {isOpen && buttonRect && (
        <DropdownMenu
          options={dropdownOptions}
          buttonRect={buttonRect}
          onClose={closeDropdown}
          buttonRef={buttonRef}
          focusNextElement
        />
      )}
    </div>
  );
});

DropdownButton.displayName = "DropdownButton";

export default DropdownButton;
